import { LayerIdEnum } from '@/utils/constant';
import { HttpStatusCode } from '@/fetch/core/constant';
import { LANE_LINE_STYLE } from '@/map-core';
import { message } from 'antd';
import { mapEditorFetch } from '../utils/fetch';
import { useSearchStore } from '../store/searchStore';
import { wgs84ToGcj02 } from '@/utils/coordTransform';
import type MapEngine from '@/map-core';

/**
 * 车道线管理器
 *
 * 层级：controller（"业务怎么做"）
 *
 * 职责：
 * 1. 车道线请求 — drawByGeometry（按几何数据）/ drawById（按限速区域 id）
 * 2. 车道线绘制 — drawLines 将 laneList 绘制到 LANE_LINE_LAYER，支持 MultiLineString
 * 3. 车道线清除 — clear 清空 LANE_LINE_LAYER
 *
 * 是 Controller 和 strategies 中唯一的 lane 清除入口
 */
export class LaneManager {
  constructor(private getMap: () => MapEngine) {}

  /** 清除 lane 线图层 */
  clear() {
    const map = this.getMap();
    const laneLayer = map.getLayer(LayerIdEnum.LANE_LINE_LAYER);
    if (laneLayer) {
      laneLayer.getSource().clear();
    }
  }

  /** 根据几何数据请求并绘制 lane 线 */
  async drawByGeometry(geometry: any) {
    const currentPosVal = useSearchStore.getState().positionSearchVal;
    if (!currentPosVal.mapId || !currentPosVal.mapVersion) {
      message.error('缺少地图信息，无法获取车道线');
      return [];
    }
    const res = await mapEditorFetch.getLaneCoordinates({
      mapId: currentPosVal.mapId,
      mapVersion: currentPosVal.mapVersion,
      geometry,
    });
    if (res.code === HttpStatusCode.Success) {
      if (res.data?.laneList?.length > 0) {
        // 传入参考几何（已知的 GCJ02 多边形），用于自动探测 lane 线坐标系
        this.drawLines(res.data.laneList, geometry);
      }
      return res.data?.laneList?.map((v: any) => v.laneId) ?? [];
    } else {
      message.error(res.message);
      return [];
    }
  }

  /** 根据 id 请求并绘制 lane 线（限速区域） */
  async drawById(id: any, referenceCoordinates?: [number, number][]) {
    const laneIdRes = await mapEditorFetch.getSpeedLimitInfo(id);
    if (
      laneIdRes.code === HttpStatusCode.Success &&
      laneIdRes.data?.laneIdList?.length > 0
    ) {
      const currentPosVal = useSearchStore.getState().positionSearchVal;
      if (!currentPosVal.mapId || !currentPosVal.mapVersion) {
        message.error('缺少地图信息，无法获取车道线');
        return;
      }
      const res = await mapEditorFetch.getLaneCoordinatesById({
        mapId: currentPosVal.mapId,
        mapVersion: currentPosVal.mapVersion,
        laneIdList: laneIdRes.data?.laneIdList,
      });
      if (
        res.code === HttpStatusCode.Success &&
        res.data?.laneList?.length > 0
      ) {
        this.drawLines(res.data.laneList, referenceCoordinates);
      } else {
        message.error(res.message);
      }
    }
  }

  /** 绘制 lane 线到图层 */
  private drawLines(
    laneList: any[],
    referenceCoordinates?: [number, number][]
  ) {
    const map = this.getMap();
    const layer = map.getLayer(LayerIdEnum.LANE_LINE_LAYER);
    const laneLayer = layer
      ? layer
      : map.createLayer(LayerIdEnum.LANE_LINE_LAYER);
    laneList.forEach((v: any) => {
      const geometry = v.geometry;
      if (!geometry || !geometry.coordinates) {
        return;
      }
      if (geometry.type === 'MultiLineString') {
        geometry.coordinates.forEach((lineCoords: any, index: number) => {
          const line = map.createLine(
            this.normalizeLaneCoordinates(lineCoords, referenceCoordinates),
            {
              color: LANE_LINE_STYLE.color,
              width: LANE_LINE_STYLE.width,
              lineDash: LANE_LINE_STYLE.lineDash,
            },
            `${v.laneId}_${index}`
          );
          laneLayer.getSource().addFeature(line);
        });
      } else {
        const line = map.createLine(
          this.normalizeLaneCoordinates(geometry.coordinates, referenceCoordinates),
          {
            color: LANE_LINE_STYLE.color,
            width: LANE_LINE_STYLE.width,
            lineDash: LANE_LINE_STYLE.lineDash,
          },
          v.laneId
        );
        laneLayer.getSource().addFeature(line);
      }
    });
  }

  /**
   * 统一 lane 线坐标格式
   *
   * 后端返回的车道线 geometry.coordinates 存在两种历史问题：
   * - 顺序可能是 [lat, lon]（非标准 GeoJSON）
   * - 坐标系可能是 WGS84（虽然接口声明 coordType=GCJ02）
   *
   * 地图上其他要素（如选中的限速区域多边形）使用 GCJ02 + [lon, lat]。
   * 因此这里：
   * 1. 按中国境内坐标范围判断是否为 [lat, lon]，若是则交换为 [lon, lat]
   * 2. 若提供了参考几何（已知 GCJ02），自动探测当前 lane 线是 WGS84 还是 GCJ02，
   *    选择更靠近参考几何的那种解释，避免后端修复后反而转错
   * 3. 无参考几何时，默认按 WGS84 处理（兼容当前后端行为）
   */
  private normalizeLaneCoordinates(
    coordinates: [number, number][],
    referenceCoordinates?: [number, number][]
  ): [number, number][] {
    if (!coordinates || coordinates.length === 0) {
      return coordinates;
    }

    const [first, second] = coordinates[0];
    // 中国境内坐标大致范围：纬度 3~54，经度 73~135
    const looksLikeLatLon =
      first >= 3 && first <= 54 && second >= 73 && second <= 135;

    const ordered: [number, number][] = looksLikeLatLon
      ? coordinates.map((point) => [point[1], point[0]])
      : coordinates;

    // 没有参考几何时，保持向后兼容：假设后端仍是 WGS84
    if (!referenceCoordinates || referenceCoordinates.length === 0) {
      return ordered.map(([lon, lat]) => wgs84ToGcj02(lon, lat));
    }

    const refCenter = this.getReferenceCenter(referenceCoordinates);

    // 分别计算：直接当 GCJ02，和从 WGS84 转 GCJ02
    const asGcj02 = ordered;
    const asWgs84 = ordered.map(([lon, lat]) => wgs84ToGcj02(lon, lat));

    const distAsGcj02 = this.squaredDistance(asGcj02[0], refCenter);
    const distAsWgs84 = this.squaredDistance(asWgs84[0], refCenter);

    return distAsGcj02 <= distAsWgs84 ? asGcj02 : asWgs84;
  }

  /** 计算参考几何的近似中心（取坐标平均值） */
  private getReferenceCenter(
    coordinates: [number, number][]
  ): [number, number] {
    let lonSum = 0;
    let latSum = 0;
    const count = coordinates.length;
    for (let i = 0; i < count; i++) {
      lonSum += coordinates[i][0];
      latSum += coordinates[i][1];
    }
    return [lonSum / count, latSum / count];
  }

  /** 两点间平方距离（用于相对比较，避免开方） */
  private squaredDistance(
    a: [number, number],
    b: [number, number]
  ): number {
    const dLon = a[0] - b[0];
    const dLat = a[1] - b[1];
    return dLon * dLon + dLat * dLat;
  }
}
